<template>
    <div id="overview-map" class="overview-map" :class="{ fullscreen: isFullScreen }">
        <vrm-mapbox
            :map-style="mapStyle"
            :location="location"
            fullscreen-target="#overview-map"
            @load="onMapboxLoaded"
            @render="onMapRender"
            @location="onLocationChanged"
            @boundsend="onBoundsChanged"
            @movestart="onMoveStart"
        ></vrm-mapbox>

        <vrm-map-layer-controls
            :layers="layersInternal"
            @layers-changed="onLayersChanged"
        ></vrm-map-layer-controls>

        <div
            v-if="$slots['filter-indicator']"
            class="mapbox-overlay__panel mapbox-overlay__filter-indicator"
        >
            <slot name="filter-indicator"></slot>
        </div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { defineComponent } from 'vue';
import VrmMapbox from './VrmMapbox';
import HtmlStatsMarker from './utils/html_stats_marker';
import HtmlInstallationPopup from './utils/html_installation_popup';
import VrmMapLayerControls from './VrmMapLayerControls';
import { vrmConfig } from '@/config';

const sourceId = 'installations';
const layerIdClusters = 'clustered-installations';
const layerIdIndividual = 'unclustered-installations';

const basicClusterFilter = ['has', 'point_count'];

/**
 * A Mapbox implementation for the Overview page.
 *
 * Pure Vue component. Cannot be used in Angular.
 */
export default defineComponent({
    name: 'VrmInstallationMap',
    components: {
        VrmMapLayerControls,
        VrmMapbox,
    },
    props: {
        /**
         * Location model for the map.
         *
         * Object containing lat, lng, zoom, bearing and pitch.
         */
        location: {
            type: Object,
            required: true,
        },
        /**
         * GeoJSON object for all installations to be shown.
         *
         * https://geojson.org/
         */
        installations: {
            type: Object,
            required: true,
            default: () => {},
        },
        /**
         * Values for the map layer filters.
         */
        layers: {
            type: Object,
            required: true,
            default: () => undefined,
        },
    },
    emits: ['zoom', 'load', 'location', 'bounds', 'layers', 'installation'],
    data: function() {
        return {
            mapStyle: vrmConfig.mapboxBaseStyleLight,
            layersInternal: {
                runtimeUntilService: true,
                tank: true,
                showOk: true,
                showWarnings: true,
                showAlarms: true,
            },
            isMoving: false,
            isFullScreen: false,
        };
    },
    created() {
        this.map = null;
        this.markers = {};
        this.markersOnScreen = {};
        this.currentPopup = null;
        this.layersInternal = Object.assign({}, this.layers);
        this.lastLayerValue = Object.assign({}, this.layers);

        this.allData = Object.assign({}, this.installations);
        this.geojson = this.installations;
    },
    watch: {
        /**
         * Called whenever the layers change.
         *
         * Update the data source and markers based on the new layer values.
         */
        layers() {
            this.layersInternal = Object.assign({}, this.layers);
        },
        /**
         * Called whenever the layers change.
         *
         * Update the data source and markers based on the new layer values.
         */
        layersInternal() {
            // Update stats markers when layers changed.
            if (this.map) {
                if (this.currentPopup) {
                    this.currentPopup.remove();
                }

                this.updateDataSource();
                this.updateMarkers();
            }
        },
        /**
         * Called whenever the installations GeoJson prop changes.
         *
         * Update the maps data source.
         *
         * @param next
         */
        installations(next) {
            this.allData = Object.assign({}, next);
            this.geojson = next;

            if (this.map) {
                this.updateDataSource();
            }
        },
    },
    methods: {
        /**
         * Update the maps data source with new GeoJSON.
         *
         * Filters the data list if warningsOnly is toggled on.
         */
        updateDataSource() {
            if (this.map) {
                const hasStatusFilters =
                    !this.layersInternal.showOk ||
                    !this.layersInternal.showWarnings ||
                    !this.layersInternal.showAlarms;

                if (hasStatusFilters) {
                    let data = Object.assign({}, this.allData);

                    const allowedStatus = [];
                    if (this.layersInternal.showOk) {
                        allowedStatus.push('ok');
                    }
                    if (this.layersInternal.showWarnings) {
                        allowedStatus.push('warning');
                    }
                    if (this.layersInternal.showAlarms) {
                        allowedStatus.push('alarm');
                    }

                    data.features = data.features.filter(i => {
                        const props = i.properties;
                        return allowedStatus.includes(props.status);
                    });

                    this.map.getSource(sourceId).setData(data);
                    this.geojson = data;
                } else {
                    this.map.getSource(sourceId).setData(this.allData);
                    this.geojson = Object.assign({}, this.allData);
                }
            }
        },
        /**
         * Called every time the map renders.
         *
         * Update the stats markers for individual installations.
         *
         * @param map
         */
        // eslint-disable-next-line no-unused-vars
        onMapRender(map) {
            if (!this.map.isSourceLoaded(sourceId)) return;
            this.updateMarkers();
        },
        /**
         * Update the stats markers whenever the enabled layers change.
         * @param layers
         */
        onLayersChanged(layers) {
            if (this.map) {
                this.layersInternal = Object.assign({}, layers);

                if (this.map.isSourceLoaded('installations')) {
                    this.updateMarkers();
                }
            }

            /**
             * Emitted whenever the selected layer values change.
             *
             * @event layers
             * @property layers
             */
            this.$emit('layers', layers);
        },
        /**
         * Callback when Mapbox had loaded.
         *
         * Add the VRM data.
         *
         * @param map
         */
        onMapboxLoaded(map) {
            /**
             * Emitted whenever mapbox has loaded.
             *
             * @event load
             * @property mapboxgl.Map
             */
            this.$emit('load', map);

            this.map = map;

            // Create data source
            this.map.addSource(sourceId, {
                type: 'geojson',
                data: this.geojson,
                cluster: true,
                clusterMaxZoom: 30, // Max zoom to cluster points on
                clusterRadius: 100, // Radius of each cluster when clustering points (defaults to 50)
                clusterProperties: {
                    has_warning: ['any', ['==', ['get', 'status'], 'warning']],
                    has_error: ['any', ['==', ['get', 'status'], 'alarm']],
                },
            });

            // Add a Symbol layer for installations that are clustered together.
            this.map.addLayer({
                id: layerIdClusters,
                type: 'symbol',
                source: sourceId,
                filter: basicClusterFilter,
                layout: {
                    'icon-allow-overlap': true,
                    'icon-image': [
                        // Use case expression with 3 steps to determine the cluster marker
                        // * If any point in the cluster has an error, show error marker
                        // * If any point in the cluster has a warning, show warning marker
                        // * Show default marker otherwise
                        'case',
                        ['boolean', ['get', 'has_error'], true],
                        'marker-cluster-error',
                        ['boolean', ['get', 'has_warning'], true],
                        'marker-cluster-warning',
                        'marker-cluster-ok',
                    ],
                    'icon-size': 1,
                    'icon-anchor': 'bottom',
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': ['Arial Unicode MS Bold'],
                    'text-size': 14,
                    'text-offset': [0, -2],
                },
            });

            // Add a Symbol layer for individual installation markers that are not in a cluster.
            this.map.addLayer({
                id: layerIdIndividual,
                type: 'symbol',
                source: 'installations',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': [
                        // Use case expression with 3 steps to determine the marker type
                        // * If installation has an alarm, show error marker
                        // * If installation has a warning for the service/tank stats, show warning marker
                        // * Show default marker otherwise
                        'case',
                        ['==', ['get', 'status'], 'alarm'],
                        'marker-individual-error',
                        ['==', ['get', 'status'], 'warning'],
                        'marker-individual-warning',
                        'marker-individual-ok',
                    ],
                    'icon-size': 1,
                    'icon-anchor': 'bottom',
                    'icon-allow-overlap': true,
                },
            });

            // Add click listeners for individual installations to open popups.
            this.map.on('click', layerIdIndividual, e => {
                // Copy coordinates array.
                const coordinates = e.features[0].geometry.coordinates.slice();
                const props = e.features[0].properties;

                // Ensure that if the map is zoomed out such that multiple
                // copies of the feature are visible, the popup appears
                // over the copy being pointed to.
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                this.currentPopup = new mapboxgl.Popup({
                    anchor: 'left',
                    offset: new mapboxgl.Point(20, -24),
                    className: 'map-popup',
                    closeButton: false,
                    maxWidth: 400,
                })
                    .setLngLat(coordinates)
                    .setDOMContent(this.buildInfoWindow([props]))
                    .addTo(this.map);
            });

            // Expand cluster on double click
            this.map.on('dblclick', layerIdClusters, e => {
                e.preventDefault();

                const features = this.map.queryRenderedFeatures(e.point, {
                    layers: [layerIdClusters],
                });

                const clusterId = features[0].properties.cluster_id;
                const currentZoom = this.map.getZoom();
                const fallbackZoom = 17;

                this.map.getSource(sourceId).getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return;

                    // If the cluster can not be expanded because they are all in the same location
                    // We zoom in to a fixed zoom or increment the current zoom by 2.
                    if (zoom >= 25) {
                        if (currentZoom < fallbackZoom) {
                            zoom = 17;
                        } else {
                            zoom = currentZoom + 2;
                        }
                    }

                    this.map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom: zoom,
                    });
                });
            });

            // Add cluster click listeners to open scrollable popups.
            this.map.on('click', layerIdClusters, e => {
                // Copy coordinates array.
                const coordinates = e.features[0].geometry.coordinates.slice();

                const features = this.map.queryRenderedFeatures(e.point, {
                    layers: [layerIdClusters],
                });

                const clusterId = features[0].properties.cluster_id;
                const point_count = features[0].properties.point_count;

                this.map
                    .getSource(sourceId)
                    .getClusterLeaves(clusterId, point_count, 0, (err, children) => {
                        if (err) return;

                        const objs = children.map(o => o.properties);

                        // Ensure that if the map is zoomed out such that multiple
                        // copies of the feature are visible, the popup appears
                        // over the copy being pointed to.
                        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                        }

                        this.currentPopup = new mapboxgl.Popup({
                            anchor: 'left',
                            offset: new mapboxgl.Point(20, -24),
                            className: 'map-popup',
                            closeButton: false,
                            maxWidth: 400,
                        })
                            .setLngLat(coordinates)
                            .setDOMContent(this.buildInfoWindow(objs))
                            .addTo(this.map);
                    });
            });

            // Remove open popup windows when zooming to prevent popups remaining visible after the
            // marker has disappeared into a cluster.
            this.map.on('zoomstart', () => {
                if (this.currentPopup) {
                    this.currentPopup.remove();
                    this.currentPopup = null;
                }
            });

            this.map.on('resize', () => {
                this.isFullScreen = !!document.fullscreenElement;
            });

            const hasStatusFilters =
                !this.layersInternal.showOk ||
                !this.layersInternal.showWarnings ||
                !this.layersInternal.showAlarms;

            if (hasStatusFilters) {
                this.updateDataSource();
            }
        },
        /**
         * Build the HTML for an info window for 1 or more installation objects.
         *
         * @param objs - List of installation(s)
         * @returns {ChildNode}
         */
        buildInfoWindow(objs) {
            const window = new HtmlInstallationPopup({
                installations: objs,
                onClick: installation => {
                    this.$emit('installation', installation);
                },
            });

            return window.buildChildNode();
        },
        /**
         * Create and update custom HTML stats markers for all non-clustered installations.
         */
        updateMarkers() {
            const newMarkers = {};
            const features = this.map.querySourceFeatures('installations');

            // If both runtime and tank are disabled, remove all stats windows.
            if (
                (!this.layersInternal.runtimeUntilService && !this.layersInternal.tank) ||
                this.lastLayerValue !== this.layersInternal ||
                this.isMoving
            ) {
                // For every marker we've added previously, remove those that are no longer visible
                for (const id in this.markersOnScreen) {
                    this.markersOnScreen[id].remove();
                }

                this.markersOnScreen = {};
                this.markers = {};

                if (this.lastLayerValue === this.layersInternal) {
                    return;
                }

                this.lastLayerValue = Object.assign({}, this.layersInternal);

                if (this.isMoving) {
                    return;
                }
            }

            // for every individual installation on the screen, create an HTML marker for it (if we didn't yet),
            // and add it to the map if it's not there already
            for (const feature of features) {
                const coords = feature.geometry.coordinates;
                const props = feature.properties;

                if (typeof props.lowestFuelTank === 'string') {
                    props.lowestFuelTank = JSON.parse(props.lowestFuelTank);
                }

                const hasFuelTankLevel =
                    props.lowestFuelTank && props.lowestFuelTank.level !== undefined;
                const hasServiceCounter = props.serviceInterval > 0;
                const hasStats =
                    (hasFuelTankLevel && this.layersInternal.tank) ||
                    (hasServiceCounter && this.layersInternal.runtimeUntilService);

                let allowedStatus = [];
                if (this.layersInternal.showOk) {
                    allowedStatus.push('ok');
                }
                if (this.layersInternal.showWarnings) {
                    allowedStatus.push('warning');
                }
                if (this.layersInternal.showAlarms) {
                    allowedStatus.push('alarm');
                }

                // Don't add stats markers for clusters, if no tank/service value exists or if the layers are both
                // turned off.
                if (props.cluster || !hasStats || !allowedStatus.includes(props.status)) {
                    continue;
                }

                const id = props.idSite;

                let marker = this.markers[id];
                if (!marker) {
                    const el = this.createStatsMarker(props);

                    marker = this.markers[id] = new mapboxgl.Marker({
                        element: el,
                        anchor: 'left',
                        offset: new mapboxgl.Point(20, -24),
                    }).setLngLat(coords);
                }
                newMarkers[id] = marker;

                if (!this.markersOnScreen[id]) marker.addTo(this.map);
            }

            // for every marker we've added previously, remove those that are no longer visible
            for (const id in this.markersOnScreen) {
                if (!newMarkers[id]) this.markersOnScreen[id].remove();
            }

            this.markersOnScreen = newMarkers;
        },
        /**
         * Create a custom HTML marker for installation stats.
         *
         * @param obj
         * @returns {ChildNode}
         */
        createStatsMarker(obj) {
            const marker = new HtmlStatsMarker({
                runtimeUntilService: obj.serviceCountDown,
                serviceInterval: obj.serviceInterval,
                tank: obj.lowestFuelTank,
                serviceWarning: obj.serviceWarning,
                runtimeUntilServiceEnabled: this.layersInternal.runtimeUntilService,
                tankEnabled: this.layersInternal.tank,
                warningsOnly: this.layersInternal.warningsOnly,
            });

            return marker.buildChildNode();
        },
        onMoveStart() {
            this.isMoving = true;

            this.updateMarkers();
        },
        onLocationChanged(location) {
            this.isMoving = false;
            this.updateMarkers();

            /**
             * Emitted whenever the map location changes and the user stops moving.
             *
             * @event location
             * @property location
             */
            this.$emit('location', location);
        },
        onBoundsChanged(bounds) {
            /**
             * Emitted whenever the map bounds change.
             *
             * @event bounds
             * @property bounds
             */
            this.$emit('bounds', bounds);
        },
    },
});
</script>
